import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Field } from '../../components/molecules/Field'
import type { ComponentRow } from '../../../viewmodels/useProductComponents'

interface Props {
  readonly open: boolean
  readonly row: ComponentRow
  readonly onClose: () => void
  readonly onSubmit: (input: {
    quantity?: number
    sortOrder?: number
  }) => Promise<{ ok: true } | { ok: false; message: string }>
}

export function ProductComponentEditModal({ open, row, onClose, onSubmit }: Props): JSX.Element {
  const [quantity, setQuantity] = useState<number>(row.quantity)
  const [sortOrder, setSortOrder] = useState<number>(row.sort_order)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dirty = quantity !== row.quantity || sortOrder !== row.sort_order

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    if (!Number.isInteger(quantity) || quantity < 1) {
      setError('Quantity must be a positive integer')
      return
    }
    if (!Number.isInteger(sortOrder) || sortOrder < 0) {
      setError('Sort order must be 0 or greater')
      return
    }
    if (!dirty) {
      onClose()
      return
    }
    setSubmitting(true)
    const patch: { quantity?: number; sortOrder?: number } = {}
    if (quantity !== row.quantity) patch.quantity = quantity
    if (sortOrder !== row.sort_order) patch.sortOrder = sortOrder
    const res = await onSubmit(patch)
    setSubmitting(false)
    if (!res.ok) setError(res.message)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit ${row.component_product_name}`}
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || !dirty}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}

        <Field
          label="Quantity"
          helper="How many units of this component go into one bundle."
          error={null}
        >
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            step={1}
            value={String(quantity)}
            onChange={(e) => {
              const raw = e.target.value.trim()
              if (raw === '') {
                setQuantity(1)
                return
              }
              const n = Number(raw)
              if (Number.isInteger(n) && n >= 1) setQuantity(n)
            }}
          />
        </Field>

        <Field label="Sort order" helper="Display order. Lower = earlier." error={null}>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={String(sortOrder)}
            onChange={(e) => {
              const raw = e.target.value.trim()
              if (raw === '') {
                setSortOrder(0)
                return
              }
              const n = Number(raw)
              if (Number.isInteger(n) && n >= 0) setSortOrder(n)
            }}
          />
        </Field>
      </div>
    </Modal>
  )
}
